import { useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { TNavigationStack } from 'app/types';
import { ActivityName } from 'app/_modules/core/types';
import { StepType, ICustomButton, IWebSocketMeasurementConfig } from '@shared/components/form/types';
import {
  MeasurementIntroStep,
  MeasurementManualStep,
  MeasurementResultStep,
  IMeasurementResultStepContext,
  IMeasurementIntroStepContext,
  IMeasurementManualStepContext,
} from '../components/activity/steps';
import { TMeasurementResponse } from 'app/_modules/pathways/entities';
import { IProcessedMeasurement } from 'app/types/measurement';
import { toMeasurementResponse } from '@shared/utils/webSocketUtils';
import { useActivitySequence } from '@shared/hooks';
import { canManualMeasurement } from 'app/_modules/core/utils';

export enum EActivitySubmitSteps {
  MEASUREMENT_INTRO_STEP = 'measurementIntroStep',
  MEASUREMENT_MANUAL_STEP = 'measurementManualStep',
  MEASUREMENT_RESULT_STEP = 'measurementResultStep',
}

interface IUseMeasurementFlowParams {
  activityName: ActivityName;
  activityId: string;
  initialStep?: number;
  forceReset?: boolean;
  isRemeasurement?: boolean;
  autoStartWebSocket?: boolean;
  hasDeviceError?: boolean;
  goToStep: (index: number) => void;
  startWebSocketListening: () => void;
}

export const useMeasurementFlow = ({
  activityName,
  activityId,
  initialStep = 0,
  forceReset = false,
  isRemeasurement = false,
  autoStartWebSocket = false,
  hasDeviceError = false,
  goToStep,
  startWebSocketListening,
}: IUseMeasurementFlowParams) => {
  const navigation = useNavigation<NativeStackNavigationProp<TNavigationStack>>();
  const [measurementResult, setMeasurementResult] = useState<TMeasurementResponse | undefined>();
  const [currentStep, setCurrentStep] = useState(initialStep);
  const { hasRemainingActivitiesInCurrentPeriod, navigateToNextOrHome } = useActivitySequence(activityId);

  const backButton: ICustomButton = {
    text: isRemeasurement ? 'screens.remeasurement.back-to-start' : 'measurement.goHome',
    onPress: () => navigation.goBack(),
    variant: 'link',
  };

  const hasRemaining = hasRemainingActivitiesInCurrentPeriod();

  const finishButton: ICustomButton | undefined = hasRemaining
    ? { text: 'measurement.nextActivity', onPress: navigateToNextOrHome, variant: 'primary' }
    : undefined;

  const supportsManualMeasurement = canManualMeasurement(activityName);

  const measurementIntroStep: StepType<EActivitySubmitSteps.MEASUREMENT_INTRO_STEP, undefined, IMeasurementIntroStepContext> = {
    component: MeasurementIntroStep,
    key: EActivitySubmitSteps.MEASUREMENT_INTRO_STEP,
    showBackButton: false,
    showNextButton: activityName !== ActivityName.STEP,
    context: { activityName, activityId },
  };

  const measurementManualStep: StepType<EActivitySubmitSteps.MEASUREMENT_MANUAL_STEP, undefined, IMeasurementManualStepContext> = {
    component: MeasurementManualStep,
    key: EActivitySubmitSteps.MEASUREMENT_MANUAL_STEP,
    showBackButton: false,
    showNextButton: false,
    context: {
      activityName,
      onSubmitSuccess: () => {
        startWebSocketListening();
      },
    },
  };

  const measurementResultStep: StepType<EActivitySubmitSteps.MEASUREMENT_RESULT_STEP, undefined, IMeasurementResultStepContext> = {
    component: MeasurementResultStep,
    key: EActivitySubmitSteps.MEASUREMENT_RESULT_STEP,
    context: { activityName, alertType: undefined, result: measurementResult, isRemeasurement },
  };

  const steps = supportsManualMeasurement
    ? [measurementIntroStep, measurementManualStep, measurementResultStep]
    : [measurementIntroStep, measurementResultStep];

  const customAction: ICustomButton | undefined =
    currentStep === 0 && supportsManualMeasurement
      ? {
          text: 'measurement.manualInputLink',
          variant: 'link',
          onPress: () => goToStep(steps.findIndex((step) => step.key === EActivitySubmitSteps.MEASUREMENT_MANUAL_STEP)),
        }
      : undefined;

  const webSocketConfig: IWebSocketMeasurementConfig = useMemo(() => ({
    activityName,
    activityId,
    resultStepKey: EActivitySubmitSteps.MEASUREMENT_RESULT_STEP,
    beforeMeasurementStepKey: EActivitySubmitSteps.MEASUREMENT_INTRO_STEP,
    onMeasurementReceived: (measurement: IProcessedMeasurement) => {
      setMeasurementResult(toMeasurementResponse(measurement));
    },
    autoStart: autoStartWebSocket,
    hasDeviceError,
  }), [activityName, activityId, autoStartWebSocket, hasDeviceError]);

  return {
    steps,
    webSocketConfig,
    backButton,
    finishButton,
    customAction,
    resolvedInitialStep: forceReset ? 0 : initialStep,
    handleFinish: navigateToNextOrHome,
    onStepChange: setCurrentStep,
  };
};
